import { Analytics } from '@cobliteam/analytics';
import {
  type CardProviderOption,
  FuelCardRequester,
  type FuelTransaction,
  GraphQLFuelCardRequester,
  NewFuelCardRequester,
  type TransactionsReportFilters,
} from '@cobliteam/api-lib';
import type {
  FuelCardAssociationResponse,
  ImportFuelTransactionsBatchInput,
  UpsertVehicleFuelConsumptionGoalInput,
  ValidateFuelTransactionsImportInput,
  ValidateFuelTransactionsImportResponse,
} from '@cobliteam/api-lib/src/__generated__/graphql';
import type { UpdateTransactionRequest } from '@cobliteam/api-lib/src/dashboard/fuelCard/Definitions/APITypes';
import type {
  DateRange,
  FuelTransactionBodyContent,
  PagedTransactionStatsByVehicle,
  PagedTransactions,
  TransactionStatsByVehicleSummary,
  TransactionStatsByVehicleSummaryByPeriod,
  TransactionSummaryBodyContent,
  TransactionSummaryBodyContentByPeriodType,
  TransactionsAvgFuelConsumptionAndCostPerKm,
  TransactionsHistoryBodyContent,
  TransactionsSummary,
  TransactionsSumTotalCostByFuelType,
  UUID,
} from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { format } from '@cobliteam/utils/date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { QUERY_KEY as VEHICLES_QUERY_KEY } from '../useEntity/useVehicle';
import { FUEL_TRANSACTION_QUERY_KEY, FUEL_TRANSACTIONS_QUERY_KEY, invalidateFuelTransactionQueries } from './invalidation';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getFuelCardRequester = () => new FuelCardRequester({ host: env.herbieApiHost });
const getNewFuelCardRequester = () => new NewFuelCardRequester({ host: env.apiHost });
const getGraphQLFuelCardRequester = () => new GraphQLFuelCardRequester();

const FUELCARD_ACTIVE_QUERY_KEY = 'fuelCardActive';
const FUELCARD_INACTIVE_QUERY_KEY = 'fuelCardInactive';

export type PhotoImportItemProgress = { status: 'uploading' } | { status: 'success'; id: string } | { status: 'error'; error: unknown };

export type PhotoImportItemResult = { ok: true; id: string } | { ok: false; error: unknown };

export interface PhotoImportVariables {
  inputs: FuelTransactionBodyContent[];
  /** Optional odometer photos (one per input by index). When present, the photo is base64-encoded
   *  and shipped on the same JSON body as the receipt. */
  odometerFiles?: (File | undefined)[];
  onItemProgress?: (index: number, progress: PhotoImportItemProgress) => void;
}

const useFuelCard = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('fuel-card');

  const useGetActiveFuelCardAssociations = () => {
    return useQuery<FuelCardAssociationResponse[]>({
      queryKey: [FUELCARD_ACTIVE_QUERY_KEY],
      queryFn: () => getGraphQLFuelCardRequester().getFuelCardAssociations('active'),
      retry: false,
      enabled: true,
      refetchOnWindowFocus: false,
    });
  };

  const useGetInactiveFuelCardAssociations = () => {
    return useQuery<FuelCardAssociationResponse[]>({
      queryKey: [FUELCARD_INACTIVE_QUERY_KEY],
      queryFn: () => getGraphQLFuelCardRequester().getFuelCardAssociations('inactive'),
      retry: false,
      enabled: true,
      refetchOnWindowFocus: false,
    });
  };

  const getFuelTransaction = (transactionId: string) => {
    return useQuery<FuelTransaction>({
      queryKey: [FUEL_TRANSACTION_QUERY_KEY, transactionId],
      queryFn: () => getNewFuelCardRequester().getFuelTransaction(transactionId),
    });
  };

  const getTransactionsHistory = (input: TransactionsHistoryBodyContent) => {
    return useQuery<PagedTransactions>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, input],
      queryFn: () => getNewFuelCardRequester().getTransactionsHistory(input),
    });
  };

  const getTransactionsSummary = (input: TransactionSummaryBodyContent) => {
    return useQuery<TransactionsSummary>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'summary', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsSummary(input),
    });
  };

  const getTransactionStatsByVehicle = (input: TransactionsHistoryBodyContent) => {
    return useQuery<PagedTransactionStatsByVehicle>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicle(input),
    });
  };

  const getTransactionStatsByVehicleSummary = (input: TransactionSummaryBodyContent) => {
    return useQuery<TransactionStatsByVehicleSummary>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle-summary', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicleSummary(input),
    });
  };

  const getTransactionStatsByVehicleSummaryByPeriod = (input: TransactionSummaryBodyContentByPeriodType) => {
    return useQuery<TransactionStatsByVehicleSummaryByPeriod[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle-summary-by-period', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicleSummaryByPeriod(input),
    });
  };

  const getTransactionsAverageFuelComsumptionAndCostPerKm = (input: TransactionSummaryBodyContentByPeriodType) => {
    return useQuery<TransactionsAvgFuelConsumptionAndCostPerKm[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'avg-fuel-consumption-and-cost-per-km', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsAvgFuelConsumptionAndCostPerKm(input),
    });
  };

  const getTransactionsSumTotalCostByFuelType = (input: TransactionSummaryBodyContentByPeriodType) => {
    return useQuery<TransactionsSumTotalCostByFuelType[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'sum-total-cost-by-fuel-type', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsSumTotalCostByFuelType(input),
    });
  };

  const useDeleteFuelCardAssociations = useMutation<void, unknown, { ids: UUID[] }>({
    mutationFn: (fuelCardDeleteContent) =>
      getGraphQLFuelCardRequester()
        .deleteFuelCardAssociation({ fuelCardIds: fuelCardDeleteContent.ids })
        .then(() => undefined),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY], refetchType: 'all' });
      Alert.success(t('associations.delete_success', { count: variables.ids.length }));
      Analytics.track('Fuel Card Association Deleted', { count: variables.ids.length });
    },
    onError: () => Alert.error(t('associations.delete_error')),
  });

  const useRestoreFuelCardAssociations = useMutation<void, unknown, { ids: UUID[] }>({
    mutationFn: (fuelCardRecoverContent) =>
      getGraphQLFuelCardRequester()
        .restoreFuelCardAssociation({ fuelCardIds: fuelCardRecoverContent.ids })
        .then(() => undefined),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY], refetchType: 'all' });
      Alert.success(t('associations.restore_success', { count: variables.ids.length }));
      Analytics.track('Fuel Card Association Restored', { count: variables.ids.length });
    },
    onError: () => Alert.error(t('associations.restore_error')),
  });

  const importTransactions = () =>
    useMutation<void, unknown, { body: FuelTransactionBodyContent; odometerFile?: File }>({
      mutationFn: ({ body, odometerFile }) => {
        const api = getNewFuelCardRequester();
        const request = odometerFile ? api.createFuelTransactionWithOdometerFile(body, odometerFile) : api.createFuelTransaction(body);
        return request.then(() => undefined);
      },
      onSuccess: () => {
        invalidateFuelTransactionQueries(queryClient);
      },
    });

  const deleteTransactions = useMutation<void, unknown, { transactionIds: string[] }>({
    mutationFn: (data) => getNewFuelCardRequester().deleteTransactions(data.transactionIds),
    onSuccess: () => {
      invalidateFuelTransactionQueries(queryClient);
    },
  });

  const requestIntegration = useMutation<void, unknown, { provider: CardProviderOption; cnpj?: string; email?: string }>({
    mutationFn: (data) => getFuelCardRequester().requestIntegration(data.provider, data.cnpj, data.email),
  });

  const updateFuelTransaction = useMutation<void, unknown, { transactionId: string; transactionData: UpdateTransactionRequest }>({
    mutationFn: (data) => getNewFuelCardRequester().updateFuelTransaction(data.transactionId, data.transactionData),
    onSuccess: () => {
      invalidateFuelTransactionQueries(queryClient);
    },
  });

  const useUpdateFuelCardAssociation = useMutation<void, unknown, { id: UUID; vehicleId?: string }>({
    mutationFn: (data) => {
      return getGraphQLFuelCardRequester()
        .updateFuelCardAssociation({ id: data.id, vehicleId: data.vehicleId })
        .then(() => undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY], refetchType: 'all' });
    },
  });

  const useValidateFuelTransactionsImport = (input: ValidateFuelTransactionsImportInput | null) => {
    return useQuery<ValidateFuelTransactionsImportResponse | undefined>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'validate-import', input],
      queryFn: () => {
        if (!input) return undefined;
        return getGraphQLFuelCardRequester().validateFuelTransactionsImport(input);
      },
      enabled: !!input,
    });
  };

  const useBatchImportFuelTransactions = () => {
    return useMutation({
      mutationFn: (input: ImportFuelTransactionsBatchInput[]) => getGraphQLFuelCardRequester().importFuelTransactions(input),
      onSuccess: () => {
        invalidateFuelTransactionQueries(queryClient);
      },
      onError: () => Alert.error(t('sheet_import.import_error')),
    });
  };

  /**
   * Sequentially creates each transaction via REST `POST /transactions/create`. Unlike the GraphQL
   * batch import, this path supports attaching the receipt image (base64) per transaction — used by
   * the photo-import flow.
   *
   * Partial-success semantics: items that succeed stay persisted (de-dupe relies on that), and per-item
   * failures are surfaced via `onItemProgress` so the UI can show which file worked and which didn't.
   * Callers receive the full `PhotoImportItemResult[]` so they can decide how to retry failed items.
   */
  const usePhotoImportFuelTransactions = () => {
    return useMutation<PhotoImportItemResult[], unknown, PhotoImportVariables>({
      mutationFn: async ({ inputs, odometerFiles, onItemProgress }) => {
        const api = getNewFuelCardRequester();
        const results: PhotoImportItemResult[] = [];

        for (let index = 0; index < inputs.length; index++) {
          const input = inputs[index];
          const odometerFile = odometerFiles?.[index];
          onItemProgress?.(index, { status: 'uploading' });
          try {
            const created = odometerFile ? await api.createFuelTransactionWithOdometerFile(input, odometerFile) : await api.createFuelTransaction(input);
            const result: PhotoImportItemResult = { ok: true, id: String(created.id) };
            results.push(result);
            onItemProgress?.(index, { status: 'success', id: result.id });
          } catch (error) {
            results.push({ ok: false, error });
            onItemProgress?.(index, { status: 'error', error });
          }
        }

        return results;
      },
      onSettled: () => {
        invalidateFuelTransactionQueries(queryClient);
      },
    });
  };

  const useUpdateFuelConsumptionGoals = () => {
    return useMutation({
      mutationFn: (data: UpsertVehicleFuelConsumptionGoalInput[]) => getGraphQLFuelCardRequester().batchSetFuelConsumptionGoals(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [VEHICLES_QUERY_KEY] });
        Alert.success(t('consumption_goal.update_success'));
      },
      onError: () => Alert.error(t('consumption_goal.update_error')),
    });
  };

  const useDownloadTransactionsReport = () => {
    return useMutation<void, unknown, { period: DateRange; vehicleGroupIds: string[]; filters?: TransactionsReportFilters }>({
      mutationFn: async ({ period, vehicleGroupIds, filters }) => {
        const blob = await getNewFuelCardRequester().getTransactionsReport(period, vehicleGroupIds, filters);
        const startTimeStr = format(period.start, 'yyMMdd');
        const endTimeStr = format(period.end, 'yyMMdd');
        const periodStr = startTimeStr === endTimeStr ? startTimeStr : `${startTimeStr}_${endTimeStr}`;
        saveAs(blob, `Cobli_${periodStr}_transacoes.xlsx`);
      },
      onError: () => Alert.error(t('report.export_error')),
    });
  };

  return {
    getFuelTransaction,
    getTransactionsHistory,
    getTransactionsSummary,
    getTransactionStatsByVehicle,
    getTransactionStatsByVehicleSummary,
    getTransactionStatsByVehicleSummaryByPeriod,
    getTransactionsAverageFuelComsumptionAndCostPerKm,
    getTransactionsSumTotalCostByFuelType,
    useGetActiveFuelCardAssociations,
    useGetInactiveFuelCardAssociations,
    importTransactions,
    useDeleteFuelCardAssociations,
    useRestoreFuelCardAssociations,
    deleteTransactions,
    requestIntegration,
    updateFuelTransaction,
    useUpdateFuelCardAssociation,
    useValidateFuelTransactionsImport,
    useBatchImportFuelTransactions,
    usePhotoImportFuelTransactions,
    useUpdateFuelConsumptionGoals,
    useDownloadTransactionsReport,
  };
};

export default useFuelCard;
